import React, { useEffect, useState } from 'react';
import { getAllPaytmTerminalsByCenterId, intitiatePaytmPayment, checkPaytmPaymentStatus } from 'core/services/api';
import Select from "react-select";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

function PaytmPOS({ centerId, invoice, transactionAmt, toggleDisplayToast, paymentType }: any) {

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCheckStatusModal, setShowCheckStatusModal] = useState<any>(false)
  const [paytmTerminalDropdownOptions, setPaytmTerminalDropdownOptions] = useState<any>([]);
  const [paytmTerminalId, setPaytmTerminalId] = useState<any>();
  const [paytmPaymentTransactionResponse, setPaytmPaymentTransactionResponse] = useState<any>()
  //Staus cehck states
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusCheckComplete, setStatusCheckComplete] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<any>();

  useEffect(() => {
    setIsLoading(true);
    getAllPaytmTerminalsByCenterId(centerId, invoice?.appointmentType)
      .then((res: any) => {
        const temp = res?.map((item: any) => {
          return {
            label: item.name,
            value: item.id,
            default: item.isDefault
          }
        })
        setPaytmTerminalDropdownOptions(temp);
        const defaultTerminal = temp.find((option: any) => option.default);
        if (defaultTerminal) {
          setPaytmTerminalId(defaultTerminal.value);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log("err", err)
        setIsLoading(false);
        toggleDisplayToast({ text: err?.response?.data?.message || "Error fetching Paytm terminals", type: "error" })
      })
  }, [])

  const getPaytmStatusMessage = (resultCode: string, resultStatus: string, resultMsg: string): string => {
    const statusMap: { [key: string]: string } = {
      '0010': 'Please check status on machine',
      '0012': 'Internal Server Error. Please retry',
      '0030': 'Please check the status on EDC machine',
      '0330': 'Invalid checksum',
      '0404': 'Transaction failed/declined',
      '0011': 'Transaction failed',
      '0000': 'Success',
      '0009': 'Payment request accepted',
      '0090': 'ECR void failed',
      '0180': 'VOID transaction not found'
    };

    return statusMap[resultCode] || resultMsg || 'Unknown status';
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'SUCCESS':
        return { color: 'green' };
      case 'FAIL':
      case 'FAILED':
        return { color: 'red' };
      case 'PENDING':
      case 'ACCEPTED_SUCCESS':
        return { color: 'orange' };
      default:
        return {};
    }
  };


  const checkPaymentStatusInsideModal = (paymentTransactionId: any) => {
    setCheckingStatus(true);
    setShowCheckStatusModal(true);
    setStatusCheckComplete(false);
    setPaymentStatus(false);

    let attempts = 0;
    const maxAttempts = 24;
    const intervalTime = 5000;

    const checkStatus = async () => {
      try {
        setIsLoading(true);
        const res = await checkPaytmPaymentStatus(paymentTransactionId, paymentType);
        setIsLoading(false);

        // Update transaction status
        setPaytmPaymentTransactionResponse(res?.data);

        const { resultCode, resultStatus, resultMsg, merchantTransactionId, transactionAmount } = res?.data || {};
        const statusMessage = getPaytmStatusMessage(resultCode, resultStatus, resultMsg);

        // Check response status
        if (resultStatus === "SUCCESS" || resultCode === "0000") {
          setPaymentStatus({ text: `Payment Successful: ₹${(Number(transactionAmount || 0) / 100).toFixed(2)} Transaction ID: ${merchantTransactionId}`, status: resultStatus });
          setStatusCheckComplete(true);
          setCheckingStatus(false);
          return true; // Stop checking on success
        } else if (resultStatus === "FAIL" || resultStatus === "FAILED" ||
          ["0011", "0330", "0404", "0090", "0180"].includes(resultCode)) {
          setPaymentStatus({ text: `Status check failed: ${statusMessage}`, status: resultStatus });
          setStatusCheckComplete(true);
          setCheckingStatus(false);
          return true; // Stop checking on failure
        } else if (resultStatus === "PENDING" || ["0010", "0030"].includes(resultCode)) {
          setPaymentStatus({ text: `${statusMessage}`, status: resultStatus });
          return false; // Continue checking
        } else if (resultStatus === "ACCEPTED_SUCCESS" || resultCode === "0009") {
          setPaymentStatus({ text: 'Payment request accepted', status: resultStatus });
          setStatusCheckComplete(true);
          setCheckingStatus(false);
          return true;
        }

        return false;
      } catch (err) {
        setIsLoading(false);
        console.error("Payment status check failed:", err);
        setPaymentStatus({ text: "Error checking payment status", status: "error" });
        return false;
      }
    };

    const timer = setInterval(async () => {
      attempts++;

      const shouldStop = await checkStatus();

      if (shouldStop || attempts >= maxAttempts) {
        clearInterval(timer);

        if (!shouldStop) {
          setPaymentStatus({ text: 'Payment Status Check Timeout', status: "ACCEPTED_SUCCESS" });
        }

        setStatusCheckComplete(true);
        setCheckingStatus(false);
      }
    }, intervalTime);

    checkStatus();

    return () => {
      clearInterval(timer);
      setCheckingStatus(false);
      setIsLoading(false);
    };
  };

  useEffect(() => {
    let cleanup: (() => void) | null = null;
    if (showCheckStatusModal && paytmPaymentTransactionResponse?.id) {
      cleanup = checkPaymentStatusInsideModal(paytmPaymentTransactionResponse.id);
    }

    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [showCheckStatusModal]);


  const getPaytmErrorMessage = (resultCode: string, resultMsg: string): string => {
    const errorMap: { [key: string]: string } = {
      '0009': 'Payment request accepted successfully',
      '0330': 'Invalid payment request, please try again',
      '1809': 'Transaction amount exceeds the allowed limit',
      '1810': 'Invalid display information',
      '0007': 'Terminal is inactive, please contact support',
      '0333': 'Terminal is busy, please try again after some time',
      '0002': 'Invalid request parameters',
      '0012': 'System error occurred, please try again',
      '0022': 'Merchant account is blocked',
      '0029': 'Invalid Merchant or Device details',
      '0233': 'Duplicate transaction, please try with different order',
      '9001': 'Merchant configuration error, please contact support',
      '0902': 'Merchant account is blocked, please contact support',
      '1825': 'Please contact support',
      '1815': 'Invalid timeout configuration, please contact support',
      '1813': 'Invalid split parameters, please contact support',
      '1814': 'Invalid timeout configuration for transaction, please contact support'
    };

    return errorMap[resultCode] || resultMsg || 'Error initiating paytm payment, please contact support';
  };

  const handleInitiatePaytmPayment = (transactionAmt: any, paytmTerminal: any) => {
    setIsLoading(true);
    intitiatePaytmPayment(centerId, paytmTerminal, { amount: transactionAmt, invoiceId: invoice?.id, paymentType: paymentType })
      .then((res: any) => {
        console.log("res", res)
        setPaytmPaymentTransactionResponse(res?.data);
        setShowCheckStatusModal(true)
        setIsLoading(false);
        if (res?.data?.resultStatus !== "ACCEPTED_SUCCESS") {
          const errorCode = res?.data?.resultCode;
          const errorMsg = res?.data?.resultMsg;
          const paytmErrorMessage = getPaytmErrorMessage(errorCode, errorMsg);
          toggleDisplayToast({ text: paytmErrorMessage, type: "error" })
          return;
        }
      })
      .catch((err: any) => {
        setIsLoading(false);
        const errorCode = err?.response?.data?.resultCode;
        const errorMsg = err?.response?.data?.resultMsg;
        const paytmErrorMessage = getPaytmErrorMessage(errorCode, errorMsg);
        toggleDisplayToast({ text: paytmErrorMessage, type: "error" })
      });
  }

  const checkStatusModalClose = () => {
    setShowCheckStatusModal(false);
    setStatusCheckComplete(false);
    setPaymentStatus(false);
    window.location.reload()
  }

  return (
    <>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            right: "50%",
          }}
        >
          <div
            style={{ width: "3rem", height: "3rem" }}
            className="spinner-border text-primary"
          ></div>
        </div>
      )}

      <div className='wrapper-inputs'>
        <div className="title-name">Machine</div>
        <div className='payment-mode-wrapper'>
          <Select
            styles={{
              control: (provided, state) => ({
                ...provided,
                border: "1px solid #ccd0e7",
                borderRadius: "10px"
              })
            }}
            className="select-payment"
            placeholder="Select Terminal"
            options={paytmTerminalDropdownOptions}
            value={paytmTerminalDropdownOptions.find((option: any) => option.value === paytmTerminalId)}
            onChange={(option: any) => {
              setPaytmTerminalId(option.value)
            }}
          />
        </div>

      </div>
      <hr />
      <div className='buttons-row'>
        <Button
          variant="primary"
          className="submission-button"
          disabled={isLoading || Number(transactionAmt) === 0 || !paytmTerminalId}
          onClick={() => {
            handleInitiatePaytmPayment(transactionAmt, paytmTerminalId)
          }}
        >
          Initiate Payment
        </Button>
      </div>
      <Modal
        show={showCheckStatusModal}
        onHide={() => {
          checkStatusModalClose()
        }}
      >
        <Modal.Header className="d-flex flex-row justify-content-center">
          <Modal.Title>
            Processing Payment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column justify-content-between align-items-center" style={{ minHeight: '100px', gap: '10px' }}>
            {!statusCheckComplete && checkingStatus && (
              <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ gap: '10px', color: 'orange' }}>
                <div> Transaction ID: {paytmPaymentTransactionResponse?.merchantTransactionId} </div>
                <div>Amount: {`₹${(Number(paytmPaymentTransactionResponse?.transactionAmount || 0) / 100).toFixed(2)}`}</div>
                <div
                  style={{ width: "2rem", height: "2rem", }}
                  className="spinner-border text-primary"
                ></div>
              </div>
            )}

            {statusCheckComplete && (
              <div style={{ textAlign: 'center', padding: '20px', lineHeight: '1.8rem' }}>
                <span style={getStatusStyle(paymentStatus?.status)}>{paymentStatus?.text}</span>
              </div>
            )}

            <div>
              <Button
                type="button"
                onClick={() => {
                  checkStatusModalClose()
                }}
                id="cancel"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}
export default PaytmPOS

